import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bell, Mail, Settings, BarChart3, Power, LogOut, Inbox, AtSign, Home, ShieldCheck, CreditCard, LineChart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const NAV = [
  { to: "/", label: "Dashboard", icon: BarChart3 },
  { to: "/inbox", label: "Posta", icon: Inbox },
  { to: "/properties", label: "Immobili", icon: Home },
  { to: "/accounts", label: "Account Email", icon: AtSign },
  { to: "/reports", label: "Resoconti", icon: Mail },
  { to: "/settings", label: "Impostazioni", icon: Settings },
  { to: "/security", label: "Sicurezza", icon: ShieldCheck },
  { to: "/billing", label: "Abbonamento", icon: CreditCard },
];

const ADMIN_NAV_ITEM = { to: "/admin", label: "Admin", icon: LineChart };

export default function Layout() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } catch (err) {
      console.error("Layout: failed to load /settings", err?.message || err);
    }
  };

  // Detect admin: try /admin/mrr — 200 = admin, 403 = normal user
  const checkAdmin = async () => {
    try {
      await api.get("/admin/mrr");
      setIsAdmin(true);
    } catch (_) {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    loadSettings();
    checkAdmin();
    const i = setInterval(loadSettings, 30000);
    return () => clearInterval(i);
  }, []);

  const handleToggle = async (val) => {
    setToggling(true);
    try {
      await api.post("/settings/toggle", { enabled: val });
      setSettings((s) => ({ ...s, master_enabled: val }));
      toast.success(val ? "Segretaria AI attiva" : "Segretaria AI disattivata");
    } catch (e) {
      toast.error("Errore durante l'aggiornamento");
    } finally {
      setToggling(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const active = settings?.master_enabled === true;

  return (
    <div className="min-h-screen flex bg-[#F9F8F6] text-[#1C1B1A]">
      {/* SIDEBAR */}
      <aside className="hidden md:flex w-64 flex-col border-r border-[#E5E4E0] bg-white">
        <div className="p-6 border-b border-[#E5E4E0] flex flex-col items-center text-center">
          <img
            src="/logo-abhome.png"
            alt="AB Home Venezia"
            className="w-24 h-24 object-contain"
            data-testid="sidebar-logo"
          />
          <h2 className="font-display text-xl mt-3 leading-tight tracking-wide" data-testid="sidebar-agency-name">
            AB HOME
          </h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C5B5A] mt-1">
            Venezia · Segretaria AI
          </p>
        </div>
        <nav className="flex-1 py-6">
          {[...NAV, ...(isAdmin ? [ADMIN_NAV_ITEM] : [])].map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2",
                  isActive
                    ? "border-[#2C4C3B] bg-[#F0EFEA] text-[#1C1B1A]"
                    : "border-transparent text-[#5C5B5A] hover:bg-[#F0EFEA] hover:text-[#1C1B1A]",
                ].join(" ")
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-6 border-t border-[#E5E4E0] text-xs">
          <div className="label-mono">Operatore</div>
          <div className="mt-1 truncate">{user?.email}</div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="mt-3 inline-flex items-center gap-2 text-[#5C5B5A] hover:text-[#C85A32] transition-colors"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* HEADER */}
        <header className="border-b border-[#E5E4E0] bg-white">
          <div className="flex items-center justify-between px-6 md:px-10 py-4 gap-4">
            <div className="md:hidden flex items-center gap-2">
              <img src="/logo-abhome.png" alt="AB Home" className="w-9 h-9 object-contain" />
              <h2 className="font-display text-lg" data-testid="mobile-agency-name">
                AB HOME
              </h2>
            </div>
            <div className="hidden md:block">
              <div className="label-mono">Centro di controllo</div>
              <p className="text-sm text-[#5C5B5A] mt-0.5">
                Risposte automatiche · Resoconti ogni 4 ore
              </p>
            </div>

            {/* MASTER TOGGLE */}
            <div
              className="master-pill"
              data-active={active}
              data-testid="master-toggle-container"
            >
              <div className="flex flex-col items-end">
                <span className="label-mono text-[10px]">
                  {active ? "Attiva" : "In pausa"}
                </span>
                <span className="font-display text-xs text-[#5C5B5A]">
                  Segretaria AI
                </span>
              </div>
              <span className={`pulse-dot ${active ? "on" : ""}`} />
              <Switch
                data-testid="master-toggle-switch"
                checked={!!active}
                disabled={toggling}
                onCheckedChange={handleToggle}
                className="data-[state=checked]:bg-[#2C4C3B]"
              />
            </div>
          </div>

          {/* MOBILE NAV */}
          <nav className="md:hidden flex overflow-x-auto border-t border-[#E5E4E0]">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  [
                    "flex-1 flex flex-col items-center gap-1 py-3 text-[11px]",
                    isActive ? "text-[#2C4C3B] bg-[#F0EFEA]" : "text-[#5C5B5A]",
                  ].join(" ")
                }
              >
                <Icon size={16} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </header>

        {/* CONTENT */}
        <main className="flex-1 p-6 md:p-10 max-w-7xl w-full mx-auto" data-testid="main-content">
          <Outlet context={{ settings, reloadSettings: loadSettings }} />
        </main>
      </div>
    </div>
  );
}
